import imgPlaceholder from "@/views/blocks/shared/images/1920x1080.svg";
import iconPlaceholder from "@/views/blocks/shared/images/20.svg";
import { computed } from "vue";
import type { BlockDef } from "./block.interfaces";
import {
    arrayField,
    backgroundImageField,
    createBlock,
    descriptiveImageField,
    headingField,
    htmlField,
    textField,
} from "./blockBuilder";
import { useDefaultColors } from "./useDefaultColors";
import {
    WithStickyProductScreenshot,
    WithStickyProductScreenshotProps,
} from "@/views/blocks/long-form/long-form";
import gridPattern from "@/views/blocks/long-form/grid-pattern.svg";

export function useLongForms() {
    const { colorDefaults } = useDefaultColors();

    const longFormBlock = computed<BlockDef>(() =>
        createBlock<WithStickyProductScreenshotProps>({
            typeId: "WithStickyProductScreenshot",
            type: "longform",
            name: "With sticky product screenshot",
            component: WithStickyProductScreenshot,
        })
            .withField(
                "backgroundImage",
                backgroundImageField("Background Image", {
                    src: gridPattern,
                    transparency: 4,
                })
            )
            .withField("tag", textField("Tag", "Streamline Operations"))
            .withField("title", headingField("Title", "Transform Business", "h2"))
            .withField(
                "description",
                htmlField(
                    "Description",
                    "Experience a revolutionary approach to business management with our integrated platform. Streamline operations, enhance productivity, and drive growth with powerful tools designed for modern enterprises."
                )
            )
            .withField(
                "image",
                descriptiveImageField("Image", { src: imgPlaceholder, alt: "Image" })
            )
            .withField(
                "topText",
                htmlField(
                    "Top text",
                    "In today's fast-paced business environment, staying competitive requires efficient tools and streamlined processes. Our platform integrates seamlessly with your existing workflows, providing powerful automation capabilities and real-time insights. Transform the way your team collaborates and delivers results with our comprehensive solution."
                )
            )
            .withArrayField(
                "features",
                arrayField(
                    "Features",
                    "features",
                    {
                        iconSrc: {
                            label: "Icon",
                            type: "image",
                        },
                        title: {
                            label: "Title",
                            type: "text",
                        },
                        description: {
                            label: "Description",
                            type: "text",
                        },
                    },
                    [
                        {
                            iconSrc: iconPlaceholder,
                            title: "Smart Automation.",
                            description:
                                "Automate repetitive tasks and workflows with intelligent systems that learn and adapt to your business needs. Save time and reduce errors while increasing productivity.",
                        },
                        {
                            iconSrc: iconPlaceholder,
                            title: "Real-time Analytics.",
                            description:
                                "Access comprehensive business insights through intuitive dashboards. Make data-driven decisions with powerful analytics tools that track performance in real-time.",
                        },
                        {
                            iconSrc: iconPlaceholder,
                            title: "Seamless Integration.",
                            description:
                                "Connect with your favorite tools and services effortlessly. Our platform works harmoniously with your existing tech stack, ensuring smooth operations.",
                        },
                    ]
                )
            )
            .withField(
                "bottomText",
                htmlField(
                    "Bottom text",
                    "Our platform adapts to your unique business needs, providing customizable solutions that grow with your organization. From startups to enterprise-level operations, we offer scalable features that ensure optimal performance at every stage of your business journey."
                )
            )
            .withField(
                "secondaryTitle",
                headingField("Secondary title", "Built for Modern Business", "h3")
            )
            .withField(
                "secondaryDescription",
                htmlField(
                    "Secondary description",
                    "Experience enterprise-grade capabilities without the complexity. Our cloud-based platform delivers powerful features through an intuitive interface, making advanced business tools accessible to teams of all sizes. Whether you're scaling up or optimizing operations, we provide the foundation for your success."
                )
            )
            .withStyleFields([
                colorDefaults.value.background,
                colorDefaults.value.text,
                colorDefaults.value.brand,
            ])
            .build()
    );

    return {
        blocks: computed(() => [longFormBlock.value]),
    };
}
